import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { auth, db } from '../../firebaseClient'; // Firebase auth
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from 'contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, profile } = useAuth(); // Using user from Firebase
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [discordUsername, setDiscordUsername] = useState('');
  const [view, setView] = useState('sign-in'); // 'sign-in', 'sign-up', 'forgot-password'
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error'

  // Redirect if already logged in
  useEffect(() => {
    // Wait until the authentication process is fully complete (loading is false)
    if (authLoading) {
      return;
    }

    // If authentication is complete and we have a user, then we can check the profile
    if (user) {
      console.log('Login page - User found:', user.uid);
      console.log('Login page - Profile:', profile);
      
      // Always redirect to landing page after login/signup
      // Users can access their dashboard via the widget on the landing page
      console.log('Login page - User authenticated, redirecting to landing page');
      navigate('/');
    }
    // If there's no user and loading is false, do nothing and stay on the login page.
  }, [user, profile, authLoading, navigate]);

  // Handle sign in with email and password
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      console.log('Attempting to sign in with email:', email);
      await signInWithEmailAndPassword(auth, email, password);

      console.log('Signin successful');
      setMessageType('success');
      setMessage('Login successful! Redirecting...');

    } catch (error) {
      console.error('Error during signin:', error);
      setMessageType('error');
      setMessage(error.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  // Create user profile in Firestore
  const createUserProfile = async (user, additionalData = {}) => {
    const userProfileRef = doc(db, 'profiles', user.uid);
    const profileSnapshot = await getDoc(userProfileRef);
    
    if (!profileSnapshot.exists()) {
      const profileData = {
        uid: user.uid,
        email: user.email,
        full_name: user.displayName || '',
        created_at: new Date(),
        is_admin: false,
        is_super_admin: false,
        is_writer: false,
        is_qa: false,
        is_client: true, // Default new users to client role
        // Discord integration fields
        discord_username: '',
        discord_id: '',
        discord_notifications: false,
        discord_connected_at: null,
        ...additionalData
      };
      
      await setDoc(userProfileRef, profileData);
      console.log('User profile created successfully');
    }
  };

  // Handle sign up with email and password
  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setMessageType('error');
      setMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      console.log('Attempting to sign up with email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore with Discord data
      await createUserProfile(userCredential.user, {
        discord_username: discordUsername.trim(),
        discord_notifications: discordUsername.trim().length > 0
      });

      // TODO: Call Firebase Cloud Function to process referral if referralCode is present
      if (referralCode) {
        console.log('Referral Code entered:', referralCode);
        // In a real app, you'd call a Cloud Function here:
        // const processReferral = httpsCallable(functions, 'processReferralSignup');
        // await processReferral({ referee_uid: auth.currentUser.uid, referral_code: referralCode });
      }

      console.log('Signup successful');
      setMessageType('success');
      setMessage('Registration successful! Please check your email for verification.');
    } catch (error) {
      console.error('Error during signup:', error);
      setMessageType('error');
      setMessage(error.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  // Handle sign in with Google
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage('');

    try {
      console.log('Attempting to sign in with Google');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create user profile for new Google users
      await createUserProfile(result.user);

      console.log('Google authentication successful');
      // Redirect will be handled by the useEffect hook
    } catch (error) {
      console.error('Error during Google signin:', error);
      setMessageType('error');
      setMessage(error.message || 'An error occurred during Google sign in');
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      console.log('Attempting to reset password for email:', email);
      await sendPasswordResetEmail(auth, email);

      console.log('Password reset email sent successfully');
      setMessageType('success');
      setMessage('Password reset instructions sent to your email!');
    } catch (error) {
      console.error('Error during password reset:', error);
      setMessageType('error');
      setMessage(error.message || 'An error occurred during password reset');
    } finally {
      setLoading(false);
    }
  };


  // Animated background circles
  const circles = Array.from({ length: 10 }, (_, i) => i);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {circles.map((i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 blur-xl"
            initial={{
              width: Math.random() * 300 + 100,
              height: Math.random() * 300 + 100,
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0.3,
            }}
            animate={{
              x: [
                Math.random() * window.innerWidth,
                Math.random() * window.innerWidth,
              ],
              y: [
                Math.random() * window.innerHeight,
                Math.random() * window.innerHeight,
              ],
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: Math.random() * 10 + 20,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-8 rounded-2xl shadow-xl">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
              <Icon name="GraduationCap" size={24} color="currentColor" />
            </div>
            <h1 className="text-2xl font-bold text-gradient font-accent ml-3">
              SOMO Academia
            </h1>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-text-primary text-center mb-6">
            {view === 'sign-in' && 'Welcome Back'}
            {view === 'sign-up' && 'Create an Account'}
            {view === 'forgot-password' && 'Reset Your Password'}
          </h2>

          {/* Message */}
          {message && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                messageType === 'success'
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-error/10 text-error border border-error/20'
              }`}
            >
              {message}
            </div>
          )}

          {/* Form */}
          {view === 'sign-in' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-text-primary">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setView('forgot-password')}
                    className="text-xs text-primary hover:text-primary-dark"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 btn-primary text-center text-lg font-bold flex items-center justify-center"
              >
                {loading ? (
                  <Icon name="Loader2" className="animate-spin mr-2" size={20} />
                ) : (
                  <Icon name="LogIn" className="mr-2" size={20} />
                )}
                Sign In
              </button>
            </form>
          )}

          {view === 'sign-up' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label htmlFor="discordUsername" className="block text-sm font-medium text-text-primary mb-1">
                  Discord Username (Optional)
                </label>
                <input
                  id="discordUsername"
                  type="text"
                  value={discordUsername}
                  onChange={(e) => setDiscordUsername(e.target.value)}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="john.doe or username#1234"
                />
                <p className="text-xs text-text-secondary mt-1">
                  💬 Get notifications and reply via Discord DMs
                </p>
              </div>

              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium text-text-primary mb-1">
                  Referral Code (Optional)
                </label>
                <input
                  id="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="Enter code if you have one"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 btn-primary text-center text-lg font-bold flex items-center justify-center"
              >
                {loading ? (
                  <Icon name="Loader2" className="animate-spin mr-2" size={20} />
                ) : (
                  <Icon name="UserPlus" className="mr-2" size={20} />
                )}
                Create Account
              </button>
            </form>
          )}

          {view === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-primary/50 focus:border-primary"
                  placeholder="your@email.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 btn-primary text-center text-lg font-bold flex items-center justify-center"
              >
                {loading ? (
                  <Icon name="Loader2" className="animate-spin mr-2" size={20} />
                ) : (
                  <Icon name="Send" className="mr-2" size={20} />
                )}
                Send Reset Instructions
              </button>
            </form>
          )}

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-border"></div>
            <span className="px-4 text-text-muted text-sm">OR</span>
            <div className="flex-grow border-t border-border"></div>
          </div>


          {/* Social Login */}
          <button
            onClick={handleGoogleSignIn} // Changed from Discord to Google
            disabled={loading}
            className="w-full py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.3v2.84C4.02 20.44 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.3C1.42 8.84 1 10.65 1 12.5s.42 3.66 1.3 5.43l3.54-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 4.02 3.56 2.3 6.21l3.54 2.84C6.71 6.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          {/* Toggle View */}
          <div className="text-center mt-6">
            {view === 'sign-in' ? (
              <p className="text-text-secondary text-sm">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => setView('sign-up')}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Sign up
                </button>
              </p>
            ) : view === 'sign-up' ? (
              <p className="text-text-secondary text-sm">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setView('sign-in')}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Sign in
                </button>
              </p>
            ) : (
              <p className="text-text-secondary text-sm">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => setView('sign-in')}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>

          {/* Back to Home */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-text-muted text-sm hover:text-text-primary flex items-center justify-center mx-auto"
            >
              <Icon name="ArrowLeft" size={16} className="mr-1" />
              Back to Home
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
